/**
 * Dashboard chart primitives. Hand-rolled SVG/CSS so the admin bundle gains no
 * chart dependency; every chart scales with its container (viewBox + % widths).
 */
import { useEffect, useId, useRef, useState } from "react";
import { tzs } from "../lib/admin";

const H = 232;
const PAD = { top: 14, right: 20, bottom: 30, left: 56 };

function axisMoney(minor: number): string {
  const major = minor / 100;
  if (major >= 1_000_000_000) return `${(major / 1_000_000_000).toFixed(1)}B`;
  if (major >= 1_000_000) return `${(major / 1_000_000).toFixed(1)}M`;
  if (major >= 1_000) return `${Math.round(major / 1_000)}k`;
  return String(Math.round(major));
}

export interface BarPoint {
  label: string;
  value: number;
}

export interface GtvBarsProps {
  points: BarPoint[];
  averageLabel?: string;
  averageWindow?: number;
}

/** Bars with a trailing moving-average line, like the design's GTV trend. */
export function GtvBars({ points, averageLabel = "7-day average", averageWindow = 7 }: GtvBarsProps) {
  const gradientId = `gtv-${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  const [hover, setHover] = useState<number | null>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(720);

  // Track the rendered width so one SVG user unit is one CSS pixel; otherwise
  // the viewBox scales axis labels down to unreadable sizes on narrow cards.
  useEffect(() => {
    const node = frameRef.current;
    if (!node) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(Math.max(240, Math.round(entry.contentRect.width)));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  if (points.length === 0) {
    return <div className="chart-empty">No sales in this period yet.</div>;
  }

  const W = width;
  const total = points.length;
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const rawMax = Math.max(0, ...points.map((point) => point.value));
  const max = rawMax > 0 ? rawMax * 1.08 : 1;
  const slot = innerW / total;
  const barW = Math.min(42, Math.max(3, slot * 0.55));
  const centerX = (index: number) => PAD.left + slot * index + slot / 2;
  const y = (value: number) => PAD.top + innerH - (value / max) * innerH;

  const averages = points.map((_, index) => {
    const start = Math.max(0, index - averageWindow + 1);
    const window = points.slice(start, index + 1);
    return window.reduce((sum, point) => sum + point.value, 0) / window.length;
  });
  const averagePath = averages
    .map((value, index) => `${index === 0 ? "M" : "L"}${centerX(index).toFixed(1)},${y(value).toFixed(1)}`)
    .join(" ");

  const labelStep = Math.max(1, Math.ceil(total / (total > 10 ? 4 : 6)));
  const activeIndex = hover === null ? null : (points[hover] ?? null);
  const activeAverage = hover === null ? 0 : (averages[hover] ?? 0);

  function handleMove(event: React.PointerEvent<SVGSVGElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    const pointerX = ((event.clientX - rect.left) / rect.width) * W;
    const index = Math.floor((pointerX - PAD.left) / slot);
    setHover(Math.max(0, Math.min(total - 1, index)));
  }

  return (
    <div className="chart-frame" ref={frameRef}>
      <svg
        className="chart-svg"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`GTV per period with ${averageLabel}`}
        onPointerMove={handleMove}
        onPointerLeave={() => setHover(null)}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#93c5fd" />
          </linearGradient>
        </defs>

        {[0, 0.25, 0.5, 0.75, 1].map((fraction) => {
          const lineY = y(max * fraction);
          return (
            <g key={fraction}>
              <line
                x1={PAD.left}
                x2={W - PAD.right}
                y1={lineY}
                y2={lineY}
                stroke="var(--ev-slate-100)"
                strokeWidth="1"
              />
              <text x={PAD.left - 10} y={lineY} textAnchor="end" dominantBaseline="middle" className="chart-axis">
                {axisMoney(max * fraction)}
              </text>
            </g>
          );
        })}

        {hover !== null && (
          <rect
            x={PAD.left + slot * hover + 1}
            y={PAD.top}
            width={Math.max(slot - 2, 2)}
            height={innerH}
            rx="4"
            fill="var(--ev-slate-100)"
            opacity="0.7"
          />
        )}

        {points.map((point, index) => {
          const height = Math.max(0, PAD.top + innerH - y(point.value));
          return (
            <rect
              key={`${point.label}-${index}`}
              x={centerX(index) - barW / 2}
              y={y(point.value)}
              width={barW}
              height={Math.max(height, point.value > 0 ? 2 : 0)}
              rx={Math.min(3, barW / 2)}
              fill={`url(#${gradientId})`}
            />
          );
        })}

        <path d={averagePath} fill="none" stroke="var(--ev-navy-900)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

        {total === 1 && (
          <circle cx={centerX(0)} cy={y(points[0]?.value ?? 0)} r="3" fill="var(--ev-navy-900)" />
        )}

        {points.map((point, index) =>
          index % labelStep === 0 || index === total - 1 ? (
            <text key={`label-${point.label}-${index}`} x={centerX(index)} y={H - 9} textAnchor="middle" className="chart-axis">
              {point.label}
            </text>
          ) : null,
        )}
      </svg>

      {activeIndex && (
        <div className="chart-tip" style={{ left: `${Math.min(88, Math.max(12, (centerX(hover ?? 0) / W) * 100))}%` }}>
          <span className="chart-tip-label">{activeIndex.label}</span>
          <span className="chart-tip-row">
            <span>GTV</span>
            <strong>{tzs(activeIndex.value)}</strong>
          </span>
          <span className="chart-tip-row">
            <span>{averageLabel}</span>
            <strong>{tzs(activeAverage)}</strong>
          </span>
        </div>
      )}
    </div>
  );
}

export interface DonutSlice {
  label: string;
  value: number;
  color: string;
  display?: string;
}

export function Donut({
  slices,
  centerTop,
  centerBottom,
}: {
  slices: DonutSlice[];
  centerTop: string;
  centerBottom: string;
}) {
  const total = slices.reduce((sum, slice) => sum + slice.value, 0);
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div className="donut-wrap">
      <div className="donut-chart">
        <svg viewBox="0 0 120 120" role="img" aria-label={`${centerTop} ${centerBottom}`}>
          <circle cx="60" cy="60" r={radius} fill="none" stroke="var(--ev-slate-100)" strokeWidth="15" />
          {slices
            .filter((slice) => slice.value > 0)
            .map((slice) => {
              const length = total ? (slice.value / total) * circumference : 0;
              const circle = (
                <circle
                  key={slice.label}
                  cx="60"
                  cy="60"
                  r={radius}
                  fill="none"
                  stroke={slice.color}
                  strokeWidth="15"
                  strokeDasharray={`${length} ${circumference - length}`}
                  strokeDashoffset={-offset}
                  transform="rotate(-90 60 60)"
                />
              );
              offset += length;
              return circle;
            })}
        </svg>
        <div className="donut-center">
          <strong>{centerTop}</strong>
          <span>{centerBottom}</span>
        </div>
      </div>
      <ul className="donut-legend">
        {slices.map((slice) => (
          <li key={slice.label}>
            <span className="donut-dot" style={{ background: slice.color }} />
            <span className="donut-name">{slice.label}</span>
            <span className="donut-value">
              {slice.display ?? (total ? `${Math.round((slice.value / total) * 100)}%` : "—")}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Sparkline({ values, color = "var(--ev-mint-500)" }: { values: number[]; color?: string }) {
  const height = 34;
  if (values.length === 0) {
    return <svg className="sparkline" viewBox={`0 0 120 ${height}`} preserveAspectRatio="none" aria-hidden="true" />;
  }
  const max = Math.max(1, ...values);
  const point = (value: number, index: number) => {
    const x = values.length <= 1 ? 60 : (index / (values.length - 1)) * 120;
    const y = height - (value / max) * (height - 8) - 4;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  };
  // A single sample has no line to draw, so show a flat mid-height stroke.
  const single = values.length === 1;
  const points = single ? `0,${height / 2} 120,${height / 2}` : values.map(point).join(" ");
  return (
    <svg className="sparkline" viewBox={`0 0 120 ${height}`} preserveAspectRatio="none" aria-hidden="true">
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {single && <circle cx="60" cy={height / 2} r="2.2" fill={color} />}
    </svg>
  );
}
